import { usePostHog } from '@posthog/react';
import type { ReactElement } from 'react';

import { focusGlobalSearch, SHORTCUT_HINT } from 'components/global-search/GlobalSearchBar';
import { AutoAwesome, SearchIcon } from 'components/icons';
import { Tooltip } from 'components/ui/tooltip';
import { useFloatingAiAvailable } from 'pages/ai-chat/floating/FloatingAiAssistant';
import { useDispatch, useSelector } from 'store';
import { setFloatingAiVisibility } from 'store/reducers/aiChat';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { FOCUS_RING } from '../focusRing';

const ROW = cn(
    'hit-area relative flex cursor-pointer items-center rounded-md transition-[scale] duration-(--press-dur) ease-out motion-safe:not-aria-[haspopup]:will-change-transform motion-safe:active:not-aria-[haspopup]:scale-(--press-scale)',
    FOCUS_RING
);

/** On the rail a row is its icon alone, named by a tooltip. */
const onRail = (collapsed: boolean, label: string, row: ReactElement) =>
    collapsed ? (
        <Tooltip content={label} side="right">
            {row}
        </Tooltip>
    ) : (
        row
    );

type Props = {
    collapsed: boolean;
};

/**
 * The sidebar's way into search and Crespect AI, above My work. Search opens the ⌘K dialog; Ask
 * Crespect AI opens the floating panel and has no shortcut of its own (the dialog's Ask row has one).
 */
function SidebarTop({ collapsed }: Props) {
    const dispatch = useDispatch();
    const posthog = usePostHog();
    const canAskAi = useFloatingAiAvailable();
    const isAiOpen = useSelector((state) => state.aiChat.floatingVisibility === 'open');
    const searchLabel = t('menu.search-or-jump');
    const askLabel = t('menu.ask-ai');

    return (
        // On touch the rows' 44px areas would overlap across `gap-1`: the gap widens so they meet.
        <div
            className={cn(
                'flex shrink-0 flex-col',
                collapsed ? 'items-center gap-0.5' : 'gap-1 px-3 pt-0.5 pb-2.5 pointer-coarse:gap-2.5'
            )}
        >
            {onRail(
                collapsed,
                searchLabel,
                <button
                    type="button"
                    data-tour="sidebar-search"
                    aria-label={collapsed ? searchLabel : undefined}
                    aria-haspopup="dialog"
                    onClick={focusGlobalSearch}
                    className={cn(
                        ROW,
                        'border-sidebar-search-border bg-paper border',
                        collapsed
                            ? 'my-px size-[34px] justify-center pointer-coarse:size-11'
                            : 'text-foreground-tertiary @container h-9 gap-2 px-2.5'
                    )}
                >
                    <SearchIcon aria-hidden className="size-4" />
                    {!collapsed && (
                        <>
                            <span className="sidebar-fade min-w-0 flex-1 truncate text-start">{searchLabel}</span>
                            {/* Only where the row holds it beside the whole label: the 256px sidebar does not. */}
                            <kbd className="sidebar-fade bg-kbd text-caption hidden rounded-[4px] px-[5px] py-0.5 font-mono leading-none @min-[230px]:inline">
                                {SHORTCUT_HINT}
                            </kbd>
                        </>
                    )}
                </button>
            )}
            {canAskAi &&
                onRail(
                    collapsed,
                    askLabel,
                    <button
                        type="button"
                        aria-label={collapsed ? askLabel : undefined}
                        onClick={() => {
                            if (!isAiOpen) {
                                posthog.capture('ai_chat_opened', { surface: 'floating' });
                            }
                            dispatch(setFloatingAiVisibility('open'));
                        }}
                        className={cn(
                            ROW,
                            'text-ai hover:bg-sidebar-hover font-medium',
                            collapsed ? 'size-9 justify-center pointer-coarse:size-11' : 'h-[34px] gap-2 px-2.5'
                        )}
                    >
                        <AutoAwesome aria-hidden className="size-4" />
                        {!collapsed && (
                            <span className="sidebar-fade min-w-0 flex-1 truncate text-start">{askLabel}</span>
                        )}
                    </button>
                )}
        </div>
    );
}

export default SidebarTop;
